import app from 'flarum/forum/app';
// ⚠️ Flarum 2.0 的 Component 在 **`flarum/common/Component`** (不在 common/components/ 下) ——
//    写成 `flarum/common/components/Component` 会让 flarum.reg.get() 返 undefined,
//    于是 `class X extends undefined` 在**模块求值期**就抛
//    「Class extends value undefined is not a constructor or null」,
//    整个 forum bundle 挂掉 ⇒ SPA 起不来 ⇒ 访问 /scheduler 被弹回首页。
//    （本仓既有组件 MyCardsTab.js / RoomsTab.js 等都是 `flarum/common/Component`。）
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Dropdown from 'flarum/common/components/Dropdown';
import Button from 'flarum/common/components/Button';
// 🔴 v0.18.47 — Flarum 原生 <Avatar> (形态 A 有图渲染 <img> / 形态 B 无图渲染首字母+user.color())
//   照抄既有范式: StatsTab.js:185-190 的 rankingRow()。
import Avatar from 'flarum/common/components/Avatar';
import Api from '../utils/Api';

/* ═══════════════════════════════════════════════════════════════════════
 * v0.18.46 — 区块标题图标 (设计稿 .card-title 的 .ct-ic)
 * ═══════════════════════════════════════════════════════════════════════
 * 图标 + 颜色**逐字照抄设计稿** (mockup 行号见每条), ⛔ 不自造。
 * ⚠️ 颜色**不走 LESS token** (扩展顶部那 9 个 token 是既定的, 不许动) ——
 *    一律**行内 style** (照设计稿 `<i class="ct-ic fas fa-xxx" style="color:#xxxxxx">` 的做法)。
 * ⚠️ cardId 是 localStorage key 的后半段 ⇒ **跨角色必须稳定**, 改 id 会让用户记住的态丢失。
 */
const SECTION_ICONS = {
  // 经营待办 (mockup 1239)
  'manager-todos': {
    icon: 'fa-list-check',
    color: 'var(--zs-warning-fbbf24, #fbbf24)'
  },
  // 🔴 v0.18.47 — 「近 7 天趋势」被**替换**成「今日课程时间轴」(设计稿 mockup 1213)
  //   ⇒ 图标跟着改成 fa-clock-rotate-left (颜色仍是 #60a5fa, 与设计稿逐字一致)。
  'manager-timeline': {
    icon: 'fa-clock-rotate-left',
    color: 'var(--zs-info-60a5fa, #60a5fa)'
  },
  // 消课金额 Top5 教练 (mockup 1255)
  'manager-ranking': {
    icon: 'fa-ranking-star',
    color: 'var(--zs-warning-fbbf24, #fbbf24)'
  },
  // 各校区经营摘要 (mockup 1782)
  'manager-schools': {
    icon: 'fa-building',
    color: 'var(--zs-info-60a5fa, #60a5fa)'
  },
  // 我的今日课表 (mockup 1375)
  'coach-today': {
    icon: 'fa-clock-rotate-left',
    color: 'var(--zs-info-60a5fa, #60a5fa)'
  },
  // 我的近期消课明细 (mockup 1420)
  'coach-charges': {
    icon: 'fa-file-lines',
    color: 'var(--zs-info-60a5fa, #60a5fa)'
  },
  // 我的待办 (mockup 1396)
  'coach-todos': {
    icon: 'fa-list-check',
    color: 'var(--zs-warning-fbbf24, #fbbf24)'
  },
  // 🔴 v0.18.47 — 「本周课时」柱状 (mockup 1406) —— 教练视图**新增的第 4 块**
  //   ⚠️ section id 是**新加的** (带后缀), 已有 id 一个都没改 ⇒ 用户记住的展开态不丢。
  'coach-week': {
    icon: 'fa-chart-column',
    color: 'var(--zs-info-60a5fa, #60a5fa)'
  },
  // 我的销售漏斗 (mockup 1516)
  'sales-funnel': {
    icon: 'fa-filter',
    color: 'var(--zs-cyan-22d3ee, #22d3ee)'
  },
  // 待跟进名单 (mockup 1549)
  'sales-followups': {
    icon: 'fa-bell',
    color: 'var(--zs-warning-fbbf24, #fbbf24)'
  },
  // 本月成交明细 (mockup 1568)
  'sales-deals': {
    icon: 'fa-coins',
    color: 'var(--zs-success-34c759, #34c759)'
  },
  // 下一节课 (mockup 1672)
  'student-next': {
    icon: 'fa-snowflake',
    color: 'var(--zs-info-38bdf8, #38bdf8)'
  },
  // 我的本周课表 (mockup 1689)
  'student-week': {
    icon: 'fa-calendar-days',
    color: 'var(--zs-info-60a5fa, #60a5fa)'
  },
  // 我的卡 (mockup 1707)
  'student-cards': {
    icon: 'fa-id-badge',
    color: 'var(--zs-success-34c759, #34c759)'
  },
  // 上课提醒 (mockup 1727)
  'student-reminders': {
    icon: 'fa-circle-info',
    color: 'var(--zs-info-60a5fa, #60a5fa)'
  }
};

/* ═══════════════════════════════════════════════════════════════════════
 * v0.18.46 — 经营待办「彩色圆点行」 (设计稿 .todo-dot, mockup 446-455 / 1239-1251)
 * ═══════════════════════════════════════════════════════════════════════
 * 圆点色按后端下发的 `key` 取 (后端 OverviewController::managerTodos / coachTodos
 * 的 key 字段: checkin / review / followup / activate), 颜色**逐字照抄设计稿**。
 * ⚠️ 找不到 key 时按 `icon` 兜底, 再找不到 ⇒ 灰 (#9ca3af) ——
 *    ⛔ **绝不静默不画点** (少一个点 = 一行看不出是什么, 设计稿里每行都有点)。
 */
const TODO_DOT_COLORS = {
  checkin: 'var(--zs-warning-fbbf24, #fbbf24)',
  // 待签到 (琥珀, 设计稿第 1 行)
  review: 'var(--zs-danger-ff6b63, #ff6b63)',
  // 待写课评 (红, 第 2 行)
  followup: 'var(--zs-purple-a78bfa, #a78bfa)',
  // 待跟进 (紫, 第 3 行)
  activate: 'var(--zs-cyan-22d3ee, #22d3ee)' // 未激活学员卡 (青, 第 4 行)
};

/** key 对不上时的 icon 兜底 (后端 icon 字段的取值是固定的四个)。 */
const TODO_ICON_FALLBACK = {
  'fa-signature': 'var(--zs-warning-fbbf24, #fbbf24)',
  'fa-comment-dots': 'var(--zs-danger-ff6b63, #ff6b63)',
  'fa-bell': 'var(--zs-purple-a78bfa, #a78bfa)',
  'fa-id-card': 'var(--zs-cyan-22d3ee, #22d3ee)'
};

/** 兜底到底的灰色 (= 设计稿 .pill-gray 同色, @text-muted)。 */
const TODO_DOT_FALLBACK = 'var(--zs-text-2-9ca3af, #9ca3af)';

/** 待办行的圆点色 (key 优先 → icon 兜底 → 灰)。 */
function todoDotColor(todo) {
  if (!todo) return TODO_DOT_FALLBACK;
  if (todo.key && TODO_DOT_COLORS[todo.key]) return TODO_DOT_COLORS[todo.key];
  if (todo.icon && TODO_ICON_FALLBACK[todo.icon]) return TODO_ICON_FALLBACK[todo.icon];
  return TODO_DOT_FALLBACK;
}

/**
 * v0.18.45 (辉哥 10/21 拍板) — 「仪表盘」tab (排课中心的默认落点, tab 条第一位)
 *
 * ## 数据来自**一个**聚合端点
 *   `GET /ziiven/scheduler/dashboard/overview` (后端 Dashboard\OverviewController)
 *   ⚠️ **不是**前端并发打 5 个既有端点 —— 那样每个端点各自返一份完整数据,
 *      「敏感字段前端不显示」= 数据已经出网关了, 辉哥的红线直接破。
 *      响应形状由**服务端按角色**裁剪 (教练看不到金额 / 学员看不到卡面额与他人数据)。
 *
 * ## KPI 跳转按权限 (辉哥原话: 「根据权限判断一下, 如果没有权限就没有跳转链接」)
 *   ⛔ **不硬编码角色白名单** —— 店长/教练/销售的权限组合后台可配, 硬编码本来就不准。
 *   ⇒ 走**现成的 fail-closed 三态 helper** `tabPermission(targetTab)`
 *     (由 SchedulerIndexPage 通过 props 传进来, 它与前端 TAB_PERMISSIONS 同源):
 *       allowed === true  ⇒ 渲染 `<a href="/scheduler/<tab>">` (可点)
 *       allowed === false ⇒ **不渲染 `<a>`**, 渲染成不可点的普通元素 (DOM 里没有那个 href)
 *       known === false   ⇒ 用户对象还没加载 ⇒ 渲染加载态
 *   ⇒ 每个 KPI 的目标 tab 由**后端**在响应里给 (`kpi.tab`), 本组件只负责按权限决定能不能点。
 *
 * ## 展开态记忆 (localStorage) —— 照抄 SalesTab.js:527-564 的现成范式, 但改成**三态**
 *   1. **key 必须带 userId** (同设备换账号不能串味)
 *   2. **三态** (v0.18.46): `'1'` ⇒ 展开 · `'0'` ⇒ 收起 · **null ⇒ 未表态**
 *      未表态时 ⇒ **桌面默认展开 / 移动端默认收起** (断点 768, 见 `isPanelMode()`)
 *   3. **整个读写包 try/catch** (隐私模式下 localStorage.getItem 本身会抛 SecurityError,
 *      抛出去连带整个 tab 白屏)
 *   4. 「读取」本身**不产生存储副作用** —— 未表态**不写回** localStorage,
 *      只有用户点标题切了态才写 `'1'` / `'0'`
 *
 * ## 移动端版式 (辉哥明确要求, 照抄真站范式, 不自创)
 *   - `isMobile()` = `window.innerWidth <= 640` (与 LessonsTab / SalesTab 同款 JS 阈值;
 *     768 是 CSS 断点, 两者语义不同, 别混)
 *   - 数值**不许裸数字独占一行** ⇒ KPI 用**带标签的 2×2 指标格**
 *   - 列表卡是**两行式**: row1 = 标题 + 状态 pill, row2 = **一行 inline**、字段间用 ` | ` 分隔
 *   - ⛔ **一个字段独占一行** ⇒ 乱; ⛔ **卡片 ≤2 行文字** (展开态除外)
 */
export default class DashboardTab extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.loading = true;
    this.error = null;
    this.data = null;
    this.range = 'week';
    // 展开态: { [cardId]: true | false | undefined }。
    //   true/false = 用户**明确**表过态 (已落 localStorage);
    //   undefined   = 未表态 ⇒ 桌面默认展开 / 移动默认收起 (解析在 isOpen())。
    //   读取不产生写副作用。
    this.expandState = {};
    this.load();
  }

  /**
   * 拉一次聚合数据。
   *
   * ⚠️ `activeSchoolId` 传的是**顶部校区下拉**当前选中的校区 (可能为 null = 跟随后端按可见校区算)。
   *   后端会把它跟 `SchoolContext::visibleSchoolIds()` 求交集, 传了不可见的校区会被**当没传**
   *   (口径抄 ListLessonsPageController L154-161), 所以前端不必自己过滤。
   */
  load() {
    this.loading = true;
    this.error = null;
    const bounds = rangeBounds(this.range);
    const params = {
      range: this.range,
      start: bounds.start,
      end: bounds.end,
      // 🔴 v0.18.47 — 「今天」也用**浏览器本地日期**给后端。
      //   理由: 生产 PHP 的 `date()` 是 UTC ⇒ 服务器自己算的「今天」在 CST 用户那里是**昨天**。
      //   后端所有「今日 / 本周 / 近 N 天」口径都跟着这个走。
      today: fmtDate(new Date())
    };
    const schoolId = this.attrs.activeSchoolId;
    if (schoolId !== null && schoolId !== undefined && schoolId !== '') {
      params.school_id = schoolId;
    }
    Api.dashboardOverview(params).then(res => {
      this.data = res && res.kpis ? res : null;
      this.error = this.data ? null : '仪表盘数据格式异常';
      this.loading = false;
      // ⛔ 「读取」不产生存储副作用 —— 这里只把**已存在的** '1' / '0' 读进内存,
      //    读不到 (= 未表态) 就是 undefined, **不**把默认值写回去。
      // ⚠️ 已在内存里的展开态**保留**（换区间重新拉数据时不该把用户刚展开的区块又收起来）;
      //    本轮新增的 section id 才从 localStorage 读。
      // ⚠️ 三态判定: 内存里**已有布尔值** (true/false) 才沿用; undefined 才去读 localStorage。
      //    读到的 '0' 必须能**覆盖桌面默认值** —— 用户显式收起过 ⇒ 桌面也收起。
      const next = {};
      this.sectionIds().forEach(id => {
        const cur = this.expandState[id];
        next[id] = cur === true || cur === false ? cur : this.readExpand(id);
      });
      this.expandState = next;
      m.redraw();
    }).catch(e => {
      this.data = null;
      this.loading = false;
      this.error = (e && e.status) === 403 ? '你没有「查看仪表盘」的权限' : '仪表盘加载失败, 请稍后重试';
      m.redraw();
    });
  }

  // ── 展开态记忆 (照抄 SalesTab.js:527-564) ──────────────────────────────────

  /**
   * 折叠态存储 key —— **必须带 userId**。
   * 同设备换账号登录时若共用一个 key, A 用户的「展开」会漏给 B 用户。
   * 取不到用户 (未登录 / session 还没就绪) 落 'guest'。
   */
  expandKey(cardId) {
    const u = app.session && app.session.user;
    let id = 'guest';
    try {
      if (u && u.id) id = String(u.id());
    } catch (e) {
      // id() 在 session 半就绪时可能抛 —— 静默落 guest, 不阻断渲染
    }
    return 'ziven-scheduler:dashboard:expand:' + id + ':' + cardId;
  }

  /**
   * 读折叠态 —— **三态** (v0.18.46)。
   *
   *   '1' ⇒ true  (展开) · '0' ⇒ false (收起) · null ⇒ **undefined (未表态)**
   *
   * ⚠️ 其它取值 ('' / 'true' / 手工改坏的乱码 / 别的 key 串过来的值) 一律当**未表态**,
   *    由 `isOpen()` 按「桌面展开 / 移动收起」兜底。
   * 整个读取包 try/catch: 隐私模式下 localStorage.getItem 本身就会抛
   * SecurityError, 抛出去会连带整个 tab 白屏。
   */
  readExpand(cardId) {
    try {
      const raw = window.localStorage.getItem(this.expandKey(cardId));
      if (raw === '1') return true;
      if (raw === '0') return false;
      return undefined;
    } catch (e) {
      return undefined;
    }
  }

  /** 写折叠态。同样 try/catch —— 配额满 / 隐私模式只读时不能阻断交互。 */
  writeExpand(cardId, open) {
    try {
      window.localStorage.setItem(this.expandKey(cardId), open ? '1' : '0');
    } catch (e) {
      // 存不进去只是「下次不记住」, 不该让点击没反应
    }
  }

  /**
   * 切换展开态。
   * ⚠️ 必须以 `isOpen()` 解析出来的**当前可见态**取反, 不能直接取内存里的原始值 ——
   *    未表态时内存里是 undefined, `!undefined` 恒为 true ⇒ 桌面点一下会「展开」(没反应),
   *    移动端点一下反而会「收起」(点开不了)。
   */
  toggleExpand(cardId) {
    const open = !this.isOpen(cardId);
    this.expandState[cardId] = open;
    this.writeExpand(cardId, open);
    m.redraw();
  }

  /**
   * 这个区块现在是不是展开的。
   *
   * v0.18.46 三态:
   *   true  ⇒ 展开 (用户显式展开过)
   *   false ⇒ 收起 (用户显式收起过 —— **桌面也收起**)
   *   undefined ⇒ 未表态 ⇒ **桌面默认展开 / 移动端默认收起**
   */
  isOpen(cardId) {
    const s = this.expandState[cardId];
    if (s === true) return true;
    if (s === false) return false;
    return !this.isPanelMode();
  }

  /**
   * 展开默认值的断点 = 768, 与仪表盘移动端 CSS 的 @media (max-width: 768px) **字面相同**。
   * ⛔ 不要用 isMobile() 的 640 —— 那是「手机版式」阈值, 决定渲染卡片列表还是桌面表格。
   *    640 判展开会在 641~768 出现「CSS 当移动端 / JS 当桌面默认展开」的错位。
   */
  isPanelMode() {
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  }

  // ── 角色对应的区块 id (同时也是 localStorage key 的后半段) ──────────────────

  /**
   * 当前视图要渲染哪些可折叠区块。
   * ⚠️ **id 必须跨角色稳定** —— 它是 localStorage key 的一部分,
   *    改了会让用户已记住的展开态「莫名其妙全收起」。
   */
  sectionIds() {
    const view = this.data && this.data.view;
    switch (view) {
      case 'manager':
        return ['manager-todos', 'manager-timeline', 'manager-ranking', 'manager-schools'];
      case 'coach':
        // 🔴 v0.18.47 — 新增 `coach-week` (「本周课时」柱状)。
        //   ⚠️ **已有 id 一个都没改** ⇒ 用户记住的展开态不丢。
        return ['coach-today', 'coach-charges', 'coach-todos', 'coach-week'];
      case 'sales':
        return ['sales-funnel', 'sales-followups', 'sales-deals'];
      default:
        return ['student-next', 'student-week', 'student-cards', 'student-reminders'];
    }
  }
  isMobile() {
    return typeof window !== 'undefined' && window.innerWidth <= 640;
  }

  // ── 区间 ────────────────────────────────────────────────────────────────────

  setRange(key) {
    if (key === this.range) return;
    this.range = key;
    this.load();
  }
  rangeLabel() {
    const labels = {
      today: '今天',
      week: '本周',
      month: '本月'
    };
    return labels[this.range] || '本周';
  }

  // ── 权限跳转 ────────────────────────────────────────────────────────────────

  /**
   * 目标 tab 能不能点?
   *
   * ⚠️ 走 SchedulerIndexPage 传进来的 `tabPermission` (fail-closed 三态)。
   *   `known === false` = 用户对象还没加载 ⇒ 一律当**不可点** (不渲染 `<a>`)。
   */
  canOpenTab(tabKey) {
    if (!tabKey) return false;
    const fn = this.attrs.tabPermission;
    if (typeof fn !== 'function') return false;
    try {
      const perm = fn(tabKey);
      return !!(perm && perm.known && perm.allowed);
    } catch (e) {
      return false;
    }
  }

  /** 跳到目标 tab (走父组件的 selectTab, 与 tab 条点击同款行为)。 */
  goTab(tabKey) {
    if (!this.canOpenTab(tabKey)) return;
    const fn = this.attrs.onSelectTab;
    if (typeof fn === 'function') fn(tabKey);
  }

  /**
   * 目标 tab 的**显示名** (跳转角标上的中文名, 如「消课统计」「课表日历」)。
   *
   * ⚠️ **不另建一张映射表** —— 名字的唯一来源是**tab 条本身**:
   *   父组件 SchedulerIndexPage 的 `buildTabList()` (走 translator 的 `tab-<key>`)。
   *   ⇒ 角标上的名字跟 tab 条上的名字**永远一致**, 改一处两处一起变。
   *   拿不到 (attrs.tabs 没传 / 找不到这个 key) ⇒ 回落 translator,
   *   再拿不到就返回 '' ⇒ 角标只画箭头, 不显示乱码。
   */
  tabLabel(tabKey) {
    if (!tabKey) return '';
    const tabs = this.attrs.tabs;
    if (Array.isArray(tabs)) {
      for (let i = 0; i < tabs.length; i++) {
        const t = tabs[i];
        if (t && t.key === tabKey && t.name) return t.name;
      }
    }
    try {
      const name = app.translator.trans('ziven-scheduler.forum.tab-' + tabKey);
      // translator 查不到 key 时会**原样返回 key 串** ⇒ 认出来就当没有
      if (name && typeof name === 'string' && name.indexOf('ziven-scheduler.') !== 0) return name;
    } catch (e) {
      // 翻译器还没就绪 ⇒ 只画箭头, 不阻断渲染
    }
    return '';
  }

  /**
   * KPI 单元格。
   *
   * v0.18.45b — 形态改成**设计稿** (mockup 1175-1206):
   *   「图标块 + 主区(标签 / 大数字)」横排, 可点的卡右上角带**跳转角标**。
   *   · 图标块底色固定 #26354c, 图标**颜色用后端下发的 kpi.iconColor** (前端不造色)
   *   · 角标内容 = 箭头 + **目标 tab 的显示名** (取自 tab 条, 不另建映射表)
   *
   * ⛔ 无权限时**不渲染 `<a>`** —— DOM 里**根本没有那个 href**
   *    (不是 `<a>` 但没有 href, 也不是 `<a href="#">`) ⇒ **也就没有跳转角标**。
   */
  kpiCell(kpi) {
    const tab = kpi.tab;
    const allowed = this.canOpenTab(tab);

    // v0.18.45b — 图标块: 底色固定 (CSS), **图标色走后端下发的 iconColor** (行内 style)。
    //   ⛔ 后端没给 iconColor 时**不写行内色** (图标继承卡片文字色), 不在前端兜底造色。
    const icon = kpi.icon ? m("div", {
      className: "ZS-dash-kpi-ic",
      style: kpi.iconColor ? {
        color: kpi.iconColor
      } : null
    }, m("i", {
      className: 'fas ' + kpi.icon
    })) : null;
    const inner = m("div", {
      className: "ZS-dash-kpi-inner"
    }, m("span", {
      className: "ZS-dash-kpi-label"
    }, kpi.label), m("span", {
      className: "ZS-dash-kpi-value"
    }, kpi.value, kpi.unit ? m("span", {
      className: "ZS-dash-kpi-unit"
    }, kpi.unit) : null), this.kpiDelta(kpi.delta));
    if (!allowed) {
      // ⛔ 无权限 ⇒ 渲染成 <div> ⇒ 没有 href、**没有跳转角标**、也没有 hover 位移
      return m("div", {
        className: "ZS-dash-kpi",
        key: kpi.key,
        title: "\u4F60\u6CA1\u6709\u8BE5\u9875\u9762\u7684\u8BBF\u95EE\u6743\u9650"
      }, icon, inner);
    }

    // v0.18.45b — 右上角跳转角标 (设计稿 .kpi-go): 箭头 + 目标 tab 的显示名
    const tabLabel = this.tabLabel(tab);
    const go = m("span", {
      className: "ZS-dash-kpi-go"
    }, m("i", {
      className: "fas fa-arrow-right"
    }), tabLabel ? m("span", {
      className: "ZS-dash-kpi-go-t"
    }, tabLabel) : null);
    return m("a", {
      className: "ZS-dash-kpi ZS-dash-kpi--link",
      key: kpi.key,
      href: '/scheduler/' + tab,
      "data-tab": tab,
      onclick: e => {
        e.preventDefault();
        this.goTab(tab);
      }
    }, icon, inner, go);
  }

  // ── v0.18.47 · 批 2 新增的 5 个渲染方法 ──────────────────────────────────────

  /**
   * KPI 涨跌行 (设计稿 .kpi-delta, mockup 1177-1203 + CSS 347-348)。
   *
   *   `<±N> <单位> vs 昨日/上周/上月`; 0 ⇒ `flat` + 「持平」。
   *
   * 🔴 **后端不下发 delta ⇒ 整行不渲染** (⛔ 不用 0 冒充、不编造)。
   *   学员视图后端**完全不下发** delta ⇒ 学员侧永远没有这一行。
   */
  kpiDelta(delta) {
    if (!delta) return null;
    const dir = delta.dir === 'down' ? 'down' : delta.dir === 'flat' ? 'flat' : 'up';
    const n = Number(delta.value || 0);
    const num = Math.abs(n).toLocaleString();
    const sign = n < 0 ? '-' : '';
    const unit = delta.unit ? ' ' + delta.unit : '';
    const period = delta.period ? ' vs ' + delta.period : '';
    const text = dir === 'flat' ? '持平' : sign + num + unit + period;
    const icon = dir === 'up' ? 'fa-arrow-trend-up' : dir === 'down' ? 'fa-arrow-trend-down' : 'fa-minus';
    return m("div", {
      className: 'ZS-dash-kpi-delta ZS-dash-kpi-delta--' + dir
    }, m("i", {
      className: 'fas ' + icon
    }), text);
  }

  /**
   * 头像 + 姓名 (设计稿 .name-cell + .ZS-avatar--sm, mockup 1221 / 1256)。
   *
   * 三种形态 (**逐字照抄**设计稿 mockup 388-402 的说明 + StatsTab.js:185-190 的既有范式):
   *   形态 A — 有上传头像      ⇒ Flarum 原生 <Avatar> 渲染 <img>
   *   形态 B — 有 model 无图   ⇒ Flarum 原生 <Avatar> 渲染首字母, 底色 = user.color()
   *   形态 C — 拿不到 model   ⇒ <span class="ZS-avatar"> 统一 @primary 蓝 + 首字母
   *
   * ⚠️ 后端额外下发了头像 URL: store 里没有 model 时也能渲染真头像 (还是形态 A)。
   */
  nameCell(name, userId, avatarUrl) {
    const label = name || '?';
    const letter = label.charAt(0).toUpperCase();
    let model = null;
    try {
      if (userId) model = app.store.getById('users', userId);
    } catch (e) {
      model = null;
    }
    let avatar;
    if (model) {
      avatar = m("span", {
        className: "ZS-avatar ZS-avatar--sm"
      }, m(Avatar, {
        user: model
      }));
    } else if (avatarUrl) {
      avatar = m("span", {
        className: "ZS-avatar ZS-avatar--sm"
      }, m("img", {
        className: "Avatar",
        src: avatarUrl,
        alt: label
      }));
    } else {
      avatar = m("span", {
        className: "ZS-avatar ZS-avatar--sm"
      }, letter);
    }
    return m("span", {
      className: "ZS-dash-name-cell"
    }, avatar, m("span", {
      className: "ZS-dash-nm"
    }, label));
  }

  /**
   * 店长「今日课程时间轴」表 (v0.18.47, 设计稿 mockup 1212-1229 + CSS 354-370)。
   *
   *   5 列: 时间 / 学员 / 教练 / 教室 / 状态
   *   时间格 = `.ZS-dash-tl-time` + 已过(t-past 绿) / 将至(t-soon 琥珀) 左边框
   */
  managerTimelineTable(lessons) {
    const now = currentTimeString();
    return m("div", {
      className: "ZS-dash-tbl-wrap"
    }, m("table", {
      className: "ZS-dash-tbl"
    }, m("thead", null, m("tr", null, m("th", {
      style: "width:120px"
    }, "\u65F6\u95F4"), m("th", null, "\u5B66\u5458"), m("th", {
      style: "width:120px"
    }, "\u6559\u7EC3"), m("th", {
      style: "width:150px"
    }, "\u6559\u5BA4"), m("th", {
      style: "width:110px"
    }, "\u72B6\u6001"))), m("tbody", null, lessons.map(l => m("tr", {
      key: l.id
    }, m("td", null, m("span", {
      className: 'ZS-dash-tl-time ' + (l.start_at && l.start_at <= now ? 'ZS-dash-tl-time--past' : 'ZS-dash-tl-time--soon')
    }, l.start_at || '—', "\u2013", l.end_at || '—')), m("td", null, this.nameCell(l.student, l.student_id, l.student_avatar)), m("td", null, this.nameCell(l.coach, l.coach_id, l.coach_avatar)), m("td", {
      className: "ZS-dash-td-muted"
    }, l.room), m("td", null, m("span", {
      className: 'ZS-dash-row-pill ZS-dash-row-pill--' + statusTone(l.status)
    }, statusLabel(l.status))))))));
  }

  /**
   * Top5 教练 (v0.18.47, 设计稿 mockup 1256-1260 + CSS 433-438)。
   *
   *   `[头像 28px] 姓名 [角色 tag] ... [数值]` + 下方 `.bl-track` / `.bl-fill`
   *
   * 🔴 进度条宽度按 **Top1 归一化** (Top1 = 100%)。
   * 🔴 **排序口径不变** —— 后端按消课课时 `charge` 排 (v0.18.45a 口径), 没改成按金额。
   */
  managerRanking(ranking, rankingHasRevenue) {
    const top = ranking.length ? parseFloat(ranking[0].charge) || 0 : 0;
    return m("div", {
      className: "ZS-dash-rank"
    }, ranking.map(r => {
      const charge = parseFloat(r.charge) || 0;
      const pct = top > 0 ? Math.max(2, Math.round(charge / top * 100)) : 2;
      return m("div", {
        className: "ZS-dash-rank-item",
        key: r.coach_id
      }, m("div", {
        className: "ZS-dash-rank-head"
      }, this.nameCell(r.name, r.coach_id, r.avatar), m("span", {
        className: 'ZS-role-tag ' + (r.role || 'coach')
      }, roleLabel(r.role)), m("span", {
        className: "ZS-dash-rank-val"
      }, r.charge, " \u8BFE\u65F6", rankingHasRevenue && r.revenue ? ' · ' + r.revenue : '')), m("span", {
        className: "ZS-dash-bl-track"
      }, m("i", {
        className: "ZS-dash-bl-fill",
        style: {
          width: pct + '%'
        }
      })));
    }));
  }

  /**
   * 校区经营摘要 (v0.18.47, 设计稿 mockup 1266-1280 + CSS 354-364)。
   *
   *   6 列: 校区 / 今日课次 / 在读学员 / 本月消课金额 / 本月课时 / 活跃率
   *
   * 🔴 `hasRevenue` = 后端 `has_money` (即 scheduler.viewStats) ⇒ 无该位时
   *   **「本月消课金额」整列不存在** (连表头都不渲染)。
   * 🔴 移动端 (isMobile 640) 走设计稿的 **2×2 格卡片** (mockup 1277):
   *   row1 = 校区名 + 活跃率 pill (**.pill.plain 无圆点**, §10.6 第 2 条)
   *          row2 = 2×2 指标格 (今日课次 / 在读学员 / 消课金额 / 本月课时)
   */
  managerSchools(schools, hasRevenue) {
    if (this.isMobile()) {
      return m("div", {
        className: "ZS-dash-campus-cards"
      }, schools.map(s => m("div", {
        className: "ZS-dash-campus-card",
        key: s.id
      }, m("div", {
        className: "ZS-dash-campus-row1"
      }, m("span", {
        className: "ZS-dash-name-cell"
      }, m("i", {
        className: "fas fa-location-dot",
        style: "color:var(--zs-danger-f87171, #f87171)"
      }), s.name), s.active_rate === undefined || s.active_rate === null ? null : m("span", {
        className: 'ZS-dash-row-pill ZS-dash-row-pill--plain ZS-dash-row-pill--' + activeRateTone(s.active_rate)
      }, s.active_rate, "%")), m("div", {
        className: "ZS-dash-campus-grid"
      }, m("div", {
        className: "ZS-dash-campus-cell"
      }, m("span", {
        className: "ZS-dash-campus-label"
      }, "\u4ECA\u65E5\u8BFE\u6B21"), m("span", {
        className: "ZS-dash-campus-value"
      }, s.lessons)), m("div", {
        className: "ZS-dash-campus-cell"
      }, m("span", {
        className: "ZS-dash-campus-label"
      }, "\u5728\u8BFB\u5B66\u5458"), m("span", {
        className: "ZS-dash-campus-value"
      }, s.active_students)), hasRevenue ? m("div", {
        className: "ZS-dash-campus-cell"
      }, m("span", {
        className: "ZS-dash-campus-label"
      }, "\u6D88\u8BFE\u91D1\u989D"), m("span", {
        className: "ZS-dash-campus-value"
      }, s.revenue)) : null, m("div", {
        className: "ZS-dash-campus-cell"
      }, m("span", {
        className: "ZS-dash-campus-label"
      }, "\u672C\u6708\u8BFE\u65F6"), m("span", {
        className: "ZS-dash-campus-value"
      }, s.hours))))));
    }
    return m("div", {
      className: "ZS-dash-tbl-wrap"
    }, m("table", {
      className: "ZS-dash-tbl"
    }, m("thead", null, m("tr", null, m("th", null, "\u6821\u533A"), m("th", {
      style: "width:110px"
    }, "\u4ECA\u65E5\u8BFE\u6B21"), m("th", {
      style: "width:110px"
    }, "\u5728\u8BFB\u5B66\u5458"), hasRevenue ? m("th", {
      style: "width:140px"
    }, "\u672C\u6708\u6D88\u8BFE\u91D1\u989D") : null, m("th", {
      style: "width:120px"
    }, "\u672C\u6708\u8BFE\u65F6"), m("th", {
      style: "width:110px"
    }, "\u6D3B\u8DC3\u7387"))), m("tbody", null, schools.map(s => m("tr", {
      key: s.id
    }, m("td", {
      className: "ZS-dash-td-strong"
    }, m("span", {
      className: "ZS-dash-name-cell"
    }, m("i", {
      className: "fas fa-location-dot",
      style: "color:var(--zs-danger-f87171, #f87171)"
    }), s.name)), m("td", {
      className: "ZS-dash-td-num"
    }, s.lessons), m("td", {
      className: "ZS-dash-td-num"
    }, s.active_students), hasRevenue ? m("td", {
      className: "ZS-dash-td-num ZS-dash-td-strong"
    }, s.revenue) : null, m("td", {
      className: "ZS-dash-td-num"
    }, s.hours), m("td", null, s.active_rate === undefined || s.active_rate === null ? null : m("span", {
      className: 'ZS-dash-row-pill ZS-dash-row-pill--' + activeRateTone(s.active_rate)
    }, s.active_rate, "%")))))));
  }

  /**
   * 销售漏斗 4 步 (v0.18.47, 设计稿 mockup 1517-1525 + CSS 470-482)。
   *
   *   4 个彩色 step + 3 个箭头 (箭头里是「下一 step ÷ 本 step」的转化率)。
   */
  salesFunnel(funnel) {
    const steps = funnel.steps || [];
    const rates = funnel.rates || [];
    const blocks = [];
    steps.forEach((s, i) => {
      blocks.push(m("div", {
        className: "ZS-dash-funnel-step",
        key: s.key,
        style: {
          background: FUNNEL_COLORS[i] || FUNNEL_COLORS[0]
        }
      }, m("b", null, s.value), m("span", null, s.label)));
      if (i < steps.length - 1) {
        blocks.push(m("div", {
          className: "ZS-dash-funnel-arrow",
          key: 'arrow-' + s.key
        }, m("span", {
          className: "ZS-dash-funnel-rate"
        }, formatRate(rates[i])), m("i", {
          className: "fas fa-arrow-right ZS-dash-funnel-arrow-icon"
        })));
      }
    });
    return m("div", {
      className: "ZS-dash-funnel-steps"
    }, blocks);
  }

  /**
   * 销售「阶段明细」表 (v0.18.47, 设计稿 mockup 1528-1537)。
   *
   *   4 列: 阶段 / 人数 / 占线索 / 较上月 (正数绿负数红)
   */
  salesStages(funnel) {
    const stages = funnel.stages || [];
    return m("div", {
      className: "ZS-dash-stage-block"
    }, m("div", {
      className: "ZS-dash-stage-title"
    }, m("i", {
      className: "fas fa-layer-group",
      style: "color:var(--zs-text-2-9ca3af, #9ca3af)"
    }), "\u9636\u6BB5\u660E\u7EC6"), m("div", {
      className: "ZS-dash-tbl-wrap"
    }, m("table", {
      className: "ZS-dash-tbl"
    }, m("thead", null, m("tr", null, m("th", null, "\u9636\u6BB5"), m("th", {
      style: "width:100px"
    }, "\u4EBA\u6570"), m("th", {
      style: "width:110px"
    }, "\u5360\u7EBF\u7D22"), m("th", {
      style: "width:120px"
    }, "\u8F83\u4E0A\u6708"))), m("tbody", null, stages.map(s => m("tr", {
      key: s.stage
    }, m("td", null, m("span", {
      className: 'ZS-dash-row-pill ZS-dash-row-pill--plain ZS-dash-row-pill--' + stageTone(s.label)
    }, s.label)), m("td", {
      className: "ZS-dash-td-num"
    }, s.count), m("td", {
      className: "ZS-dash-td-num ZS-dash-td-muted"
    }, formatRate(s.pct)), m("td", {
      className: 'ZS-dash-td-num ZS-dash-td-' + (s.delta > 0 ? 'up' : s.delta < 0 ? 'down' : 'flat')
    }, s.delta > 0 ? '+' + s.delta : s.delta)))))));
  }

  /**
   * 教练「本周课时」柱状 (v0.18.47, 设计稿 mockup 1406-1415 + CSS 463-468)。
   *
   *   7 根柱 = 一~日, 当天那根加 is-today; 右侧副标题「共 N 课时」。
   */
  coachWeekChart(week) {
    const days = week || [];
    const max = days.reduce((m, x) => Math.max(m, parseFloat(x.value) || 0), 0);
    return m("div", {
      className: "ZS-dash-col-chart"
    }, days.map((d, i) => {
      const v = parseFloat(d.value) || 0;
      const h = max > 0 ? Math.max(3, Math.round(v / max * 100)) : 3;
      return m("div", {
        className: 'ZS-dash-cc-col' + (d.is_today ? ' is-today' : ''),
        key: i
      }, m("span", {
        className: "ZS-dash-cc-val"
      }, d.value), m("span", {
        className: "ZS-dash-cc-bar",
        style: v > 0 ? {
          height: h + '%'
        } : {
          height: '3px',
          background: 'var(--zs-border-2f343c, #2f343c)'
        }
      }), m("span", {
        className: "ZS-dash-cc-lab"
      }, d.label));
    }));
  }

  // ── 区块 (可折叠) ────────────────────────────────────────────────────────────
  /**
   * 一个可折叠区块。
   *
   * v0.18.46 — 标题**前置图标** (设计稿 .card-title 的 .ct-ic, mockup 327-332):
   *   `<i class="ct-ic fas fa-xxx" style="color:#xxxxxx"></i>` 在**标题前面**。
   *   ⚠️ 图标色**走行内 style** (照设计稿的做法), **不**写进 LESS token
   *      (扩展的 9 个 token 是既定的, 不许动)。
   *
   * @param string   cardId  localStorage key 的后半段 (必须跨角色稳定)
   * @param string   title   区块标题
   * @param number   count  条数 (徽标)
   * @param string   summary 收起态的一行摘要 (**≤1 行**, 移动端规范)
   * @param function children 展开态内容
   * @param string   emptyText 没有数据时的文案
   * @param number   emptyCount 数据条数 (用来判断要不要渲染空态)
   * @param string   icon     图标类 (如 'fa-list-check'); 不传则查 SECTION_ICONS
   * @param string   iconColor 图标颜色 (如 '#fbbf24'); 不传则查 SECTION_ICONS
   */
  section(cardId, title, count, summary, children, emptyText, emptyCount, icon, iconColor) {
    const open = this.isOpen(cardId);
    const hasData = typeof emptyCount === 'number' ? emptyCount > 0 : true;

    // 图标取值: 显式参数优先, 否则查模块级的 SECTION_ICONS 表 (单一真源)。
    const meta = SECTION_ICONS[cardId] || {};
    const ic = icon || meta.icon || null;
    const icColor = iconColor || meta.color || null;
    return m("div", {
      className: 'ZS-dash-section' + (open ? ' is-open' : ''),
      key: cardId
    }, m("div", {
      className: "ZS-dash-section-head",
      role: "button",
      tabIndex: 0,
      "aria-expanded": open ? 'true' : 'false',
      onclick: () => this.toggleExpand(cardId),
      onkeydown: e => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          this.toggleExpand(cardId);
        }
      }
    }, ic ? m("i", {
      className: 'ZS-dash-section-ic fas ' + ic,
      style: icColor ? {
        color: icColor
      } : null
    }) : null, m("span", {
      className: "ZS-dash-section-title"
    }, title), typeof count === 'number' ? m("span", {
      className: "ZS-dash-section-count"
    }, count) : null, m("span", {
      className: "ZS-dash-section-summary"
    }, summary), m("i", {
      className: 'fas fa-chevron-down ZS-dash-caret' + (open ? ' is-open' : '')
    })), m("div", {
      className: "ZS-dash-section-wrap"
    }, m("div", {
      className: "ZS-dash-section-clip"
    }, m("div", {
      className: "ZS-dash-section-body"
    }, hasData ? children : m("div", {
      className: "ZS-dash-empty"
    }, emptyText)))));
  }

  /**
   * 一行列表项 (移动端两行式: row1 = 标题 + 状态, row2 = 一行 inline, ` | ` 分隔)
   *
   * v0.18.45b — pill 改成**设计稿形态**: 配色类 + (可选) 前置圆点。
   *
   * ⛔ v0.19.26d — 设计稿里的 `purple` 变体**代码从未产出** (statusTone / stageTone /
   *   cardTone / 内联字面量 全都不返回它) ⇒ 对应的 `.ZS-dash-row-pill--purple`
   *   已判死并在 v0.19.26c 删除。将来若要恢复 purple 变体, 需同时补回那条 LESS 规则。
   *
   *   @param string|null pill     pill 文案 (null = 不渲染 pill)
   *   @param string      tone     配色: green/amber/red/blue/gray/cyan (照设计稿)
   *   @param boolean     dot      是否画前置圆点。设计稿里**状态** pill 有圆点、
   *                             **销售阶段** pill 没有 (`.pill.plain`) ⇒ 传 false
   */
  listRow(title, pill, fields, tone, dot) {
    const pillClass = 'ZS-dash-row-pill ZS-dash-row-pill--' + (tone || 'gray') + (dot === false ? ' ZS-dash-row-pill--plain' : '');
    return m("div", {
      className: "ZS-dash-row",
      key: title + (pill || '')
    }, m("div", {
      className: "ZS-dash-row1"
    }, m("span", {
      className: "ZS-dash-row-title"
    }, title), pill ? m("span", {
      className: pillClass
    }, pill) : null), fields && fields.length ? m("div", {
      className: "ZS-dash-row2"
    }, fields.map((f, i) => m("span", {
      key: i
    }, i > 0 ? m("span", {
      className: "ZS-dash-row-divider"
    }, " | ") : null, f))) : null);
  }

  // ── 各角色的区块内容 ────────────────────────────────────────────────────────

  managerSections() {
    const d = this.data;
    const todos = d.todos || [];
    const todoRows = todos.map(t => {
      const allowed = this.canOpenTab(t.tab);
      // v0.18.46 — 设计稿 .todo-row (mockup 446-455 / 1239-1251):
      //   彩色圆点 + 文案 + 数字 + 右箭头。
      const inner = m('[', null, m("span", {
        className: "ZS-dash-todo-dot",
        style: {
          background: todoDotColor(t)
        }
      }), m("span", {
        className: "ZS-dash-todo-label"
      }, t.label), m("span", {
        className: "ZS-dash-todo-count"
      }, t.count), allowed ? m("i", {
        className: "fas fa-chevron-right ZS-dash-todo-arrow"
      }) : null);
      const row = m("div", {
        className: "ZS-dash-todo"
      }, inner);
      if (!allowed) {
        return m("div", {
          className: "ZS-dash-todo-wrap",
          key: t.key
        }, row);
      }
      return m("a", {
        className: "ZS-dash-todo-wrap",
        key: t.key,
        href: '/scheduler/' + t.tab,
        "data-tab": t.tab,
        onclick: e => {
          e.preventDefault();
          this.goTab(t.tab);
        }
      }, row);
    });

    // 🔴 v0.18.47 — 店长主区: 「近 7 天趋势」**替换**成设计稿的「今日课程时间轴」。
    //   ⚠️ section id **保持 `manager-timeline` 不变** ⇒ 用户记住的展开态不丢。
    const timeline = d.today_lessons || [];
    const ranking = d.coach_ranking || [];
    const schools = d.schools || [];

    // 🔴 v0.18.45a — 教练榜**排序一直是按消课课时** (charge), `revenue` 只是附加列。
    //   服务端用 `has_money` 显式告知「这份响应里有没有金额」(⚠️ **权限驱动**, 不是数据驱动 ——
    //   否则「有 viewStats 但当期没消课数据」的店长, 标题会随数据有无一天一变)。
    //   无 scheduler.viewStats ⇒ 标题改成「消课课时排行」, 免得让人以为是金额榜。
    const rankingHasRevenue = !!d.has_money;
    return [this.section('manager-todos', '待办', todos.length, todos.filter(t => t.count > 0).length ? todos.filter(t => t.count > 0).length + ' 项待处理' : '暂无待办', m("div", {
      className: "ZS-dash-todos"
    }, todoRows), '暂无待办', todos.length), this.section('manager-timeline', '今日课程时间轴', timeline.length, timeline.length ? timeline.length + ' 节课' : '今天没有课', this.managerTimelineTable(timeline), '今天没有课', timeline.length), this.section('manager-ranking', rankingHasRevenue ? '教练排行' : '消课课时排行', ranking.length, ranking.length ? ranking[0].name + ' 领先' : '暂无数据', this.managerRanking(ranking, rankingHasRevenue), '暂无数据', ranking.length), this.section('manager-schools', '校区', schools.length, schools.length ? schools.length + ' 个校区' : '暂无校区', this.managerSchools(schools, rankingHasRevenue), '暂无校区', schools.length)];
  }
  coachSections() {
    const d = this.data;
    const today = d.today_lessons || [];
    const charges = d.recent_charges || [];
    const todos = d.todos || [];
    // 🔴 v0.18.47 — 「本周课时」柱状 (教练视图第 4 块)
    const week = d.week_charges || [];
    return [this.section('coach-today', '今日课次', today.length, today.length ? today.length + ' 节课' : '今天没有课', m("div", {
      className: "ZS-dash-rows"
    }, today.map(l => this.listRow(l.start_at || '—', statusLabel(l.status), [l.student, l.room, l.lesson_charge + ' 课时'].filter(Boolean), statusTone(l.status), true))), '今天没有课', today.length), this.section('coach-charges', '最近消课', charges.length, charges.length ? charges.length + ' 条记录' : '暂无记录', m("div", {
      className: "ZS-dash-rows"
    }, charges.map(l => this.listRow((l.date || '') + ' ' + (l.start_at || ''), statusLabel(l.status), [l.student, l.card, l.lesson_charge + ' 课时'].filter(Boolean), statusTone(l.status), true))), '暂无记录', charges.length), this.section('coach-todos', '待办', todos.length, todos.filter(t => t.count > 0).length ? todos.filter(t => t.count > 0).length + ' 项待处理' : '暂无待办', m("div", {
      className: "ZS-dash-todos"
    }, todos.map(t => {
      const allowed = this.canOpenTab(t.tab);
      const row = m("div", {
        className: "ZS-dash-todo"
      }, m("span", {
        className: "ZS-dash-todo-dot",
        style: {
          background: todoDotColor(t)
        }
      }), m("span", {
        className: "ZS-dash-todo-label"
      }, t.label), m("span", {
        className: "ZS-dash-todo-count"
      }, t.count), allowed ? m("i", {
        className: "fas fa-chevron-right ZS-dash-todo-arrow"
      }) : null);
      if (!allowed) {
        return m("div", {
          className: "ZS-dash-todo-wrap",
          key: t.key
        }, row);
      }
      return m("a", {
        className: "ZS-dash-todo-wrap",
        key: t.key,
        href: '/scheduler/' + t.tab,
        "data-tab": t.tab,
        onclick: e => {
          e.preventDefault();
          this.goTab(t.tab);
        }
      }, row);
    })), '暂无待办', todos.length),
    // 🔴 v0.18.47 — 「本周课时」柱状 (教练视图**新增的第 4 块**)
    //   ⚠️ section id `coach-week` 是**新加的** (带后缀), 已有 id 一个都没动。
    this.section('coach-week', '本周课时', week.length, '共 ' + (d.week_total || '0') + ' 课时', this.coachWeekChart(week), '本周暂无课时', week.length)];
  }
  salesSections() {
    const d = this.data;
    const funnel = d.funnel || {};
    const followups = d.followups || [];
    const deals = d.deals || [];
    return [this.section('sales-funnel', '销售漏斗', (funnel.steps || []).length, (funnel.total || 0) + ' 条跟进', m("div", null, this.salesFunnel(funnel), this.salesStages(funnel)), '暂无数据', (funnel.steps || []).length), this.section('sales-followups', '最近跟进', followups.length, followups.length ? followups.length + ' 条记录' : '暂无记录', m("div", {
      className: "ZS-dash-rows"
    }, followups.map(f => this.listRow(f.customer, f.stage_label, [f.next_followup_at ? '下次 ' + f.next_followup_at : null, f.created_at].filter(Boolean),
    // v0.18.45b — 销售阶段 pill: 设计稿里就是**无圆点**的那类 (`.pill.plain`)
    stageTone(f.stage_label), false))), '暂无记录', followups.length), this.section('sales-deals', '成交', deals.length, deals.length ? deals.length + ' 条' : '暂无成交', m("div", {
      className: "ZS-dash-rows"
    }, deals.map(x => this.listRow(x.customer, x.kind === 'card' ? '新签卡' : '已成交', [x.created_at].filter(Boolean), x.kind === 'card' ? 'blue' : 'green', false))), '暂无成交', deals.length)];
  }
  studentSections() {
    const d = this.data;
    const next = d.next_lesson;
    const week = d.week_lessons || [];
    const cards = d.my_cards || [];
    const reminders = d.reminders || [];
    return [this.section('student-next', '下一节课', next ? 1 : 0, next ? (next.date || '') + ' ' + (next.start_at || '') : '暂无安排', next ? m("div", {
      className: "ZS-dash-rows"
    }, this.listRow((next.date || '') + ' ' + (next.start_at || ''), statusLabel(next.status), [next.room, next.lesson_charge + ' 课时'].filter(Boolean), statusTone(next.status), true)) : null, '暂无安排', next ? 1 : 0), this.section('student-week', '本周课程', week.length, week.length ? week.length + ' 节课' : '本周没有课', m("div", {
      className: "ZS-dash-rows"
    }, week.map(l => this.listRow((l.date || '') + ' ' + (l.start_at || ''), statusLabel(l.status), [l.room, l.lesson_charge + ' 课时'].filter(Boolean), statusTone(l.status), true))), '本周没有课', week.length), this.section('student-cards', '我的卡片', cards.length, cards.length ? cards.length + ' 张卡' : '暂无卡片', m("div", {
      className: "ZS-dash-rows"
    }, cards.map(c => this.listRow(c.name, c.is_expired ? '已过期' : c.is_activated ? '生效中' : '未激活', [c.remaining_lessons + ' / ' + c.total_lessons + ' 课时', c.expires_at ? c.expires_at + ' 到期' : '永久有效'], cardTone(c), true))), '暂无卡片', cards.length), this.section('student-reminders', '提醒', reminders.length, reminders.length ? reminders.length + ' 条提醒' : '暂无提醒', m("div", {
      className: "ZS-dash-rows"
    }, reminders.map(r => this.listRow(r.label, null, []))), '暂无提醒', reminders.length)];
  }

  /* v0.18.78 — 作用域提示 pill (`.ZS-dash-scope`) **整体下线** (辉哥 10/24 指令:
   *   「把 .ZS-dash-scope 内容都去掉」)。
   *   原为 v0.18.45a/45b 的三态 pill (admin / 零分配 / manager), 唯一作用是
   *   「明示我能看到几个校区」。本方法 + LESS 四条规则 + 后端 `scope` 字段
   *   **三处同步删除** ⇒ 勿只删 DOM 留下孤儿样式, 也勿只删样式留下孤儿字段。
   *   📌 若日后要恢复, 见 `OverviewController` 里 v0.18.45a 的留档注释
   *     (零分配时 `visibleSchoolIds()` 的 `[-1]` 哨兵会被算成 1 ⇒ 提示语说谎)。 */

  view() {
    if (this.loading) {
      return m("div", {
        className: "ZS-dash"
      }, m(LoadingIndicator, null));
    }
    if (this.error) {
      return m("div", {
        className: "ZS-dash"
      }, m("div", {
        className: "ZS-dash-empty"
      }, this.error));
    }
    if (!this.data) {
      return m("div", {
        className: "ZS-dash"
      }, m("div", {
        className: "ZS-dash-empty"
      }, "\u6682\u65E0\u6570\u636E"));
    }
    const view = this.data.view;
    let sections;
    if (view === 'manager') sections = this.managerSections();else if (view === 'coach') sections = this.coachSections();else if (view === 'sales') sections = this.salesSections();else sections = this.studentSections();

    // 🔴 v0.18.45a — 店长没有 viewStats 时 KPI 只有 3 张 (金额那张整卡不出现)。
    //   桌面栅格是 4 列 ⇒ 3 张会空出一格 ⇒ 加个修饰类改成 3 列 (移动端仍是 2×2)。
    const kpis = this.data.kpis || [];
    const kpiGridClass = 'ZS-dash-kpis' + (kpis.length === 3 ? ' ZS-dash-kpis--3' : '');
    return m("div", {
      className: "ZS-dash"
    }, m("div", {
      className: "ZS-dash-head"
    }, m(Dropdown, {
      buttonClassName: "ZS-dash-range-btn",
      icon: "fas fa-calendar-days",
      label: this.rangeLabel()
    }, [{
      key: 'today',
      label: '今天'
    }, {
      key: 'week',
      label: '本周'
    }, {
      key: 'month',
      label: '本月'
    }].map(r => m(Button, {
      key: r.key,
      active: r.key === this.range,
      onclick: () => this.setRange(r.key)
    }, r.label)))), m("div", {
      className: kpiGridClass
    }, kpis.map(k => this.kpiCell(k))), m("div", {
      className: "ZS-dash-sections"
    }, sections));
  }
}

/** 区间 → 本地日期字面量 (后端只认 YYYY-MM-DD 字面量, 不用它自己算「今天」)。 */
export function rangeBounds(range) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  let start;
  let end;
  if (range === 'today') {
    start = new Date(y, m, d);
    end = start;
  } else if (range === 'month') {
    start = new Date(y, m, 1);
    end = new Date(y, m + 1, 0);
  } else {
    // week: 周一为一周之始 (与后端 resolveRange 的 'N' 口径一致)
    const dow = (now.getDay() + 6) % 7; // 0 = 周一
    start = new Date(y, m, d - dow);
    end = new Date(y, m, d + (6 - dow));
  }
  return {
    start: fmtDate(start),
    end: fmtDate(end)
  };
}

/** Date → 'YYYY-MM-DD' (本地时区, 不用 toISOString —— 那是 UTC, 会差一天)。 */
export function fmtDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + d;
}

/** 课次状态 → 中文标签 (只覆盖本扩展实际用到的几个取值)。 */
function statusLabel(status) {
  switch (status) {
    case 'scheduled':
      return '已预约';
    case 'completed':
      return '已消课';
    case 'checked-in':
      return '已签到';
    case 'cancelled':
      return '已取消';
    default:
      return status || '';
  }
}

/* ═══════════════════════════════════════════════════════════════════════
 * v0.18.45b — pill 配色映射 (照设计稿的 pill 配色表, 不自创)
 * ═══════════════════════════════════════════════════════════════════════
 * 配色类只有 6 个 (设计稿 mockup 378-384): green/amber/red/blue/gray/cyan。
 * ⛔ v0.19.26d — 设计稿配色表里的 `purple` 变体**代码从未产出** ⇒ 不在这 6 个里;
 *   对应的 `.ZS-dash-row-pill--purple` 已判死并在 v0.19.26c 删除。
 * ⚠️ 下面每一个映射都**能在设计稿里找到同款配色**:
 *   已签到/已消课 → green (mockup 1221 / 1432)
 *   待签到        → amber (mockup 1222 / 2057)
 *   已预约        → gray  (mockup 1223)
 *   待上课        → blue (mockup 1699) / gray (另一处)
 *   阶段: 潜在客户=gray / 已联系=blue / 已约课=amber / 已试课=cyan / 已成交=green / 无意向=red
 *          (mockup 1531-1536, 1554-1558 —— 阶段 pill 在设计稿里就是**无圆点**的那类)
 */

/** 课次状态 → pill 配色 (状态 pill: **带**前置圆点)。 */
function statusTone(status) {
  switch (status) {
    case 'completed':
    case 'checked-in':
      return 'green';
    case 'scheduled':
      return 'gray';
    case 'cancelled':
      return 'red';
    default:
      return 'gray';
  }
}

/** 销售阶段 → pill 配色 (阶段 pill: **不带**圆点)。 */
function stageTone(label) {
  switch (label) {
    case '已联系':
      return 'blue';
    case '有意向':
      return 'gray';
    case '体验中':
      return 'cyan';
    case '已约课':
      return 'amber';
    case '已成交':
      return 'green';
    case '无意向':
      return 'red';
    default:
      return 'gray';
  }
}

/** 学员卡片状态 → pill 配色 (状态 pill: **带**前置圆点)。 */
function cardTone(card) {
  if (!card) return 'gray';
  if (card.is_expired) return 'red';
  if (card.is_activated) return 'green';
  return 'gray';
}

/* ═══════════════════════════════════════════════════════════════════════
 * v0.18.47 — 批 2 新增的模块级 helper
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * 销售漏斗 4 步的底色 (**逐字照抄设计稿** mockup 1518-1524)。
 *   线索 #6b7280 · 跟进 #3b82f6 · 体验课 #14b8a6 · 成交 #22c55e
 */
const FUNNEL_COLORS = ['var(--zs-text-3-6b7280, #6b7280)', 'var(--zs-brand-3b82f6, #3b82f6)', 'var(--zs-cyan-14b8a6, #14b8a6)', 'var(--zs-success-22c55e, #22c55e)'];

/**
 * 角色 → 角色 tag 文案 (设计稿 .ZS-role-tag, mockup 1256)。
 *   coach 绿「教练」/ manager 紫「店长」/ sales 橙「销售」
 */
function roleLabel(role) {
  switch (role) {
    case 'manager':
      return '店长';
    case 'sales':
      return '销售';
    default:
      return '教练';
  }
}

/**
 * 活跃率 pill 三档 (辉哥 10/21 拍板): **≥70% 绿 / ≥40% 琥珀 / <40% 红**。
 */
function activeRateTone(rate) {
  const r = Number(rate);
  if (!isFinite(r)) return 'gray';
  if (r >= 70) return 'green';
  if (r >= 40) return 'amber';
  return 'red';
}

/** 百分数 → '60.5%' (一位小数, 设计稿 mockup 1519 的写法)。 */
function formatRate(v) {
  const n = Number(v);
  if (!isFinite(n)) return '0%';
  return (Math.round(n * 10) / 10).toFixed(1) + '%';
}

/** 当前时间的 'HH:MM' (浏览器本地时区) —— 用来判「已过 / 将至」。 */
function currentTimeString() {
  const d = new Date();
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/components/DashboardTab', DashboardTab);